import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Copy, Clock, Ticket } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/* Admin panel — automated daily VIP drop monitor.
 * The system mints 10 codes every 24h automatically — no manual generation. */
export default function RoyaiCodesAdmin() {
  const [codes, setCodes] = useState([]);
  const [drop, setDrop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      const [codesRes, dropRes] = await Promise.all([
        api.get("/admin/royai-codes"),
        api.get("/royai/vip/drop"),
      ]);
      setCodes(codesRes.data.codes || []);
      setDrop(dropRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    const s = setInterval(load, 30000);
    return () => {
      clearInterval(t);
      clearInterval(s);
    };
  }, []);

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch {
      toast.error("Clipboard blocked — copy manually.");
    }
  };

  const nextDropIn = drop?.next_drop_at
    ? Math.max(0, Math.floor((new Date(drop.next_drop_at).getTime() - Date.now()) / 1000))
    : 0;
  const nextDropHms = `${String(Math.floor(nextDropIn / 3600)).padStart(2, "0")}:${String(
    Math.floor((nextDropIn % 3600) / 60),
  ).padStart(2, "0")}:${String(nextDropIn % 60).padStart(2, "0")}`;

  return (
    <div className="mb-12" data-testid="royai-codes-admin">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <KeyRound size={16} className="text-amber-500" />
            <div className="text-overline">RoyAI VIP Drop · Automated</div>
          </div>
          <div className="text-xs text-neutral-500">
            The system mints <span className="text-white">10 fresh codes</span> every 24h at
            <span className="text-amber-400"> 00:00 UTC</span>. Each code expires 60 minutes after a user claims it.
          </div>
        </div>
      </div>

      {drop && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5 border border-white/5 mb-4" data-testid="drop-summary">
          <div className="bg-[#0c0c0c] p-4">
            <Ticket size={14} className="text-amber-500 mb-2" />
            <div className="font-display text-2xl font-medium text-amber-500">
              {drop.remaining}<span className="text-neutral-600 text-base"> / {drop.total}</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Remaining Today</div>
          </div>
          <div className="bg-[#0c0c0c] p-4">
            <KeyRound size={14} className="text-emerald-400 mb-2" />
            <div className="font-display text-2xl font-medium text-white">{drop.claimed}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Claimed</div>
          </div>
          <div className="bg-[#0c0c0c] p-4">
            <Clock size={14} className="text-amber-500 mb-2" />
            <div className="font-mono-tech text-lg text-amber-400" data-testid="next-drop-timer">{nextDropHms}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Next Drop In</div>
          </div>
          <div className="bg-[#0c0c0c] p-4">
            <div className="font-display text-2xl font-medium text-white">{drop.ttl_minutes}m</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Code Lifetime</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-[#0c0c0c] border border-white/5 p-6 text-sm text-neutral-500">Loading codes…</div>
      ) : codes.length === 0 ? (
        <div className="bg-[#0c0c0c] border border-white/5 p-6 text-sm text-neutral-500">
          No codes yet — the daily drop will mint 10 on the next scheduler tick.
        </div>
      ) : (
        <div className="border border-white/5" data-testid="royai-codes-list">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-[#050505] text-[10px] uppercase tracking-widest text-neutral-500 font-mono-tech">
            <div className="col-span-3">Code</div>
            <div className="col-span-3">Status</div>
            <div className="col-span-3">Expires In</div>
            <div className="col-span-3 text-right">Action</div>
          </div>
          {codes.map((c) => {
            const remainingLive =
              !c.expires_at
                ? null
                : Math.max(0, Math.floor((new Date(c.expires_at).getTime() - Date.now()) / 1000));
            const isExpired = remainingLive === 0;
            const isUnclaimed = remainingLive === null;
            const mm = remainingLive == null ? "--" : String(Math.floor(remainingLive / 60)).padStart(2, "0");
            const ss = remainingLive == null ? "--" : String(remainingLive % 60).padStart(2, "0");
            void tick;
            return (
              <div
                key={c.code}
                className="grid grid-cols-12 gap-2 items-center px-4 py-3 border-t border-white/5 text-sm"
                data-testid={`royai-code-${c.code}`}
              >
                <div className="col-span-3 font-mono-tech tracking-[0.3em] text-white">{c.code}</div>
                <div className="col-span-3 text-xs font-mono-tech">
                  {isUnclaimed ? (
                    <span className="text-neutral-500">Unclaimed</span>
                  ) : isExpired ? (
                    <span className="text-red-400">Expired</span>
                  ) : (
                    <span className="text-amber-400">Claimed · Active</span>
                  )}
                </div>
                <div className="col-span-3 font-mono-tech">
                  {isUnclaimed ? (
                    <span className="text-neutral-600">—</span>
                  ) : isExpired ? (
                    <span className="text-red-400">00:00</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-amber-400">
                      <Clock size={11} /> {mm}:{ss}
                    </span>
                  )}
                </div>
                <div className="col-span-3 text-right">
                  <button
                    onClick={() => copy(c.code)}
                    data-testid={`copy-code-${c.code}`}
                    className="inline-flex items-center gap-2 border border-white/10 text-neutral-300 hover:text-white px-3 py-1.5 text-xs uppercase tracking-widest font-semibold"
                  >
                    <Copy size={11} /> Copy
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
